import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getActiveOrganization, reportApiNotice } from '../utils/activeOrganization';
import type {
  Appointment,
  AppointmentWithDetails,
  CheckIn,
  CheckInStatus,
  QueueItem,
  QueuePriority,
  PatientSearchResult,
  ReceptionistStats,
  ApiResponse,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests using AWS Cognito session
api.interceptors.response.use((r) => r, (error) => {
  reportApiNotice(error);
  return Promise.reject(error);
});

api.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const activeOrg = getActiveOrganization();
    if (activeOrg?.id && config.headers) {
      config.headers['X-Organization-Id'] = activeOrg.id;
    }
  } catch (error) {
    console.log('No active session');
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

export const receptionistService = {
  // Today's Appointments
  getTodayAppointments: async (organizationId?: string): Promise<AppointmentWithDetails[]> => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get<AppointmentWithDetails[]>(
      `/api/receptionist/appointments/today`,
      { params }
    );
    return response.data || [];
  },

  // Get appointments by date range
  getAppointmentsByDateRange: async (
    startDate: string,
    endDate: string,
    organizationId?: string
  ): Promise<AppointmentWithDetails[]> => {
    const params = { startDate, endDate, ...(organizationId && { organizationId }) };
    const response = await api.get<AppointmentWithDetails[]>(
      `/api/receptionist/appointments`,
      { params }
    );
    return response.data || [];
  },

  // Get appointment details
  getAppointmentDetails: async (appointmentId: string): Promise<AppointmentWithDetails> => {
    const response = await api.get<AppointmentWithDetails>(
      `/api/appointments/${appointmentId}`
    );
    return response.data;
  },

  // Create appointment
  createAppointment: async (data: {
    patientId: string;
    doctorId: string;
    appointmentDate: string;
    appointmentTime: string;
    reason: string;
    notes?: string;
    organizationId?: string;
  }): Promise<Appointment> => {
    const response = await api.post<Appointment>(
      `/api/appointments`,
      data
    );
    return response.data;
  },

  // Update appointment
  updateAppointment: async (
    appointmentId: string,
    data: Partial<Appointment>
  ): Promise<Appointment> => {
    const response = await api.put<Appointment>(
      `/api/appointments/${appointmentId}`,
      data
    );
    return response.data;
  },

  // Cancel appointment
  cancelAppointment: async (appointmentId: string, reason?: string): Promise<Appointment> => {
    const response = await api.put<Appointment>(
      `/api/appointments/${appointmentId}/cancel`,
      { reason }
    );
    return response.data;
  },

  // Reschedule appointment
  rescheduleAppointment: async (
    appointmentId: string,
    newDate: string,
    newTime: string
  ): Promise<Appointment> => {
    const response = await api.put<Appointment>(
      `/api/appointments/${appointmentId}/reschedule`,
      { appointmentDate: newDate, appointmentTime: newTime }
    );
    return response.data;
  },

  // Check-in Management
  checkInPatient: async (appointmentId: string, patientId: string, notes?: string): Promise<CheckIn> => {
    const response = await api.post<CheckIn>(
      `/api/receptionist/checkin`,
      { appointmentId, patientId, notes }
    );
    return response.data;
  },

  checkOutPatient: async (checkInId: string): Promise<CheckIn> => {
    const response = await api.put<CheckIn>(
      `/api/receptionist/checkin/${checkInId}/checkout`
    );
    return response.data;
  },

  updateCheckInStatus: async (checkInId: string, status: CheckInStatus): Promise<CheckIn> => {
    const response = await api.put<CheckIn>(
      `/api/receptionist/checkin/${checkInId}/status`,
      { status }
    );
    return response.data;
  },

  getCheckInDetails: async (checkInId: string): Promise<CheckIn> => {
    const response = await api.get<CheckIn>(
      `/api/receptionist/checkin/${checkInId}`
    );
    return response.data;
  },

  getCheckInByAppointment: async (appointmentId: string): Promise<CheckIn | null> => {
    try {
      const response = await api.get<CheckIn>(
        `/api/receptionist/checkin/appointment/${appointmentId}`
      );
      return response.data;
    } catch {
      return null;
    }
  },

  // Queue Management
  getQueueStatus: async (organizationId?: string): Promise<QueueItem[]> => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get<QueueItem[]>(
      `/api/receptionist/queue`,
      { params }
    );
    return response.data || [];
  },

  // Token board for the waiting-room screen (no patient names)
  getQueueDisplay: async (): Promise<{
    updatedAt: string;
    doctors: { doctorId: string; doctorName: string; nowServing?: number; next: number[]; waiting: number }[];
  }> => {
    const response = await api.get('/api/receptionist/queue/display');
    return response.data;
  },

  updateQueuePriority: async (queueId: string, priority: QueuePriority): Promise<QueueItem> => {
    const response = await api.put<QueueItem>(
      `/api/receptionist/queue/${queueId}/priority`,
      { priority }
    );
    return response.data;
  },

  // Patient Search
  searchPatients: async (query: string, organizationId?: string): Promise<PatientSearchResult[]> => {
    const params = { query, ...(organizationId && { organizationId }) };
    const response = await api.get<PatientSearchResult[]>(
      `/api/patients/search`,
      { params }
    );
    return response.data || [];
  },

  // Statistics
  getReceptionistStats: async (date?: string, organizationId?: string): Promise<ReceptionistStats> => {
    const params = { ...(date && { date }), ...(organizationId && { organizationId }) };
    const response = await api.get<ReceptionistStats>(
      `/api/receptionist/stats`,
      { params }
    );
    return response.data;
  },

  // Get available time slots for doctor
  getAvailableTimeSlots: async (doctorId: string, date: string): Promise<string[]> => {
    const response = await api.get<string[]>(
      `/api/doctors/${doctorId}/available-slots`,
      { params: { date } }
    );
    return response.data || [];
  },

  // Get all doctors in organization
  getDoctors: async (organizationId?: string): Promise<any[]> => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get<any[]>(
      `/api/doctors`,
      { params }
    );
    return response.data || [];
  },

  // Create a new patient
  createPatient: async (data: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string;
    gender: string;
    weight?: number;
    bloodGroup?: string;
    address?: string;
    abhaNumber?: string;
    abhaAddress?: string;
    emergencyContact?: {
      name: string;
      relationship: string;
      phone: string;
    };
    consentGiven?: boolean;
  }): Promise<any> => {
    // Convert to format expected by PatientsController
    const payload = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      dateOfBirth: data.dateOfBirth,
      gender: data.gender,
      weight: data.weight,
      bloodGroup: data.bloodGroup,
      address: data.address,
      abhaNumber: data.abhaNumber,
      abhaAddress: data.abhaAddress,
      emergencyContact: data.emergencyContact ? {
        name: data.emergencyContact.name,
        relation: data.emergencyContact.relationship,
        phone: data.emergencyContact.phone
      } : undefined,
      consentGiven: !!data.consentGiven,
    };
    const response = await api.post<any>(
      `/api/patients`,
      payload
    );
    return response.data;
  },
};
